import { useEffect, useId, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api";
import {
  createAdminQuestion,
  updateAdminQuestion,
  type AdminPracticeQuestion,
  type AdminPracticeQuestionInput,
} from "@/lib/admin-api";
import { activeStates } from "@/lib/states";
import { LANGUAGES } from "@/lib/languages";
import ChoiceEditor from "@/components/admin/ChoiceEditor";
import QuestionPreviewCard from "@/components/admin/QuestionPreviewCard";
import { track } from "@/lib/analytics";

/**
 * apps/web :: components/admin :: QuestionEditorForm
 *
 * Create + edit form for a practice question. Owns all form state,
 * validates client-side (char counts, choices-count, exactly-one-correct),
 * submits to POST or PUT per `mode`. Server-side validation errors come
 * back as `ApiError` with `body = { error, field, message }`; we surface
 * `message` inline on the matching field.
 *
 * Field errors use `aria-live="polite"` + `aria-invalid` + `aria-describedby`
 * so screen-reader users hear the validation message as soon as it lands
 * without needing to re-focus the offending control (spec: "keyboard-only
 * path works end-to-end").
 *
 * The live `<QuestionPreviewCard>` is rendered alongside the form in the
 * parent page — not inside this component — because the editor page owns
 * the two-column layout. This component exposes its in-memory `draft`
 * via `onDraftChange` so the preview reflects keystroke-level edits.
 */

const CATEGORY_ENUM = [
  "RIGHT_OF_WAY",
  "SPEED_LIMITS",
  "SIGNS_AND_SIGNALS",
  "IMPAIRED_DRIVING",
  "PARKING",
  "SHARING_THE_ROAD",
  "SCHOOL_ZONES",
  "LANE_CHANGES",
  "EMERGENCY_VEHICLES",
] as const;

const MAX_QUESTION_TEXT = 1000;
const MAX_EXPLANATION = 2000;
const MAX_CHOICES = 4;
const MIN_CHOICES = 2;

export const EMPTY_QUESTION_INPUT: AdminPracticeQuestionInput = {
  state_code: "CA",
  locale: "en",
  category: "RIGHT_OF_WAY",
  question_text: "",
  choices: [
    { id: "a", text: "" },
    { id: "b", text: "" },
  ],
  correct_choice_id: "a",
  explanation: "",
  handbook_section: null,
  difficulty: 3,
  reviewed_by: null,
  reviewed_at_ms: null,
};

export interface QuestionEditorFormProps {
  mode: "create" | "edit";
  initial: AdminPracticeQuestionInput;
  /** Only set in edit mode — the server-assigned id. */
  questionId?: string;
  onSaved: (saved: AdminPracticeQuestion) => void;
  /** Keystroke-level draft propagation for the live-preview panel. */
  onDraftChange?: (draft: AdminPracticeQuestionInput) => void;
}

/** Map backend field names (camelCase from PracticeAdminRoutes) to the
 *  form field key so the inline error renders on the right control. */
function resolveErrorField(field: string | undefined): string | null {
  if (!field) return null;
  // Backend surfaces "stateCode" / "questionText" / ... — our local form
  // state uses snake_case wire shape. Normalise both halves so the lookup
  // key is stable.
  const map: Record<string, string> = {
    stateCode: "state_code",
    locale: "locale",
    category: "category",
    questionText: "question_text",
    choices: "choices",
    correctChoiceId: "correct_choice_id",
    explanation: "explanation",
    difficulty: "difficulty",
  };
  return map[field] ?? field;
}

export default function QuestionEditorForm({
  mode,
  initial,
  questionId,
  onSaved,
  onDraftChange,
}: QuestionEditorFormProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<AdminPracticeQuestionInput>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<{
    field: string;
    message: string;
  } | null>(null);
  const radioGroupName = useId();
  const qTextId = useId();
  const explanationId = useId();
  const handbookId = useId();
  const difficultyId = useId();
  const qTextErrId = useId();
  const explanationErrId = useId();
  const difficultyErrId = useId();
  const choicesErrId = useId();

  const update = (patch: Partial<AdminPracticeQuestionInput>) => {
    setDraft((d) => {
      const next = { ...d, ...patch };
      onDraftChange?.(next);
      return next;
    });
  };

  const onChoiceText = (i: number, text: string) => {
    setDraft((d) => {
      const choices = d.choices.slice();
      choices[i] = { ...choices[i], text };
      const next = { ...d, choices };
      onDraftChange?.(next);
      return next;
    });
  };

  const onSelectCorrect = (i: number) => {
    setDraft((d) => {
      const next = { ...d, correct_choice_id: d.choices[i]?.id ?? d.correct_choice_id };
      onDraftChange?.(next);
      return next;
    });
  };

  const onRemoveChoice = (i: number) => {
    if (draft.choices.length <= MIN_CHOICES) return;
    setDraft((d) => {
      const choices = d.choices.slice();
      choices.splice(i, 1);
      // If we just removed the correct row, default to the first choice.
      const stillHasCorrect = choices.some(
        (c) => c.id === d.correct_choice_id,
      );
      const correct_choice_id = stillHasCorrect
        ? d.correct_choice_id
        : choices[0]?.id ?? "";
      const next = { ...d, choices, correct_choice_id };
      onDraftChange?.(next);
      return next;
    });
  };

  const onAddChoice = () => {
    if (draft.choices.length >= MAX_CHOICES) return;
    setDraft((d) => {
      const nextLetter = String.fromCharCode(97 + d.choices.length);
      const choices = [...d.choices, { id: nextLetter, text: "" }];
      const next = { ...d, choices };
      onDraftChange?.(next);
      return next;
    });
  };

  const validateClient = (): { field: string; message: string } | null => {
    if (
      !draft.question_text.trim() ||
      draft.question_text.length > MAX_QUESTION_TEXT
    ) {
      return {
        field: "question_text",
        message: `1-${MAX_QUESTION_TEXT} chars required`,
      };
    }
    if (
      !draft.explanation.trim() ||
      draft.explanation.length > MAX_EXPLANATION
    ) {
      return {
        field: "explanation",
        message: `1-${MAX_EXPLANATION} chars required`,
      };
    }
    if (draft.choices.length < MIN_CHOICES || draft.choices.length > MAX_CHOICES) {
      return {
        field: "choices",
        message: `${MIN_CHOICES}-${MAX_CHOICES} choices required`,
      };
    }
    if (draft.choices.some((c) => !c.text.trim())) {
      return { field: "choices", message: "every choice needs text" };
    }
    if (!draft.choices.some((c) => c.id === draft.correct_choice_id)) {
      return {
        field: "correct_choice_id",
        message: "one correct choice required",
      };
    }
    if (draft.difficulty < 1 || draft.difficulty > 5) {
      return { field: "difficulty", message: "1-5 required" };
    }
    return null;
  };

  const handleSubmit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    setFieldError(null);
    const clientErr = validateClient();
    if (clientErr) {
      setFieldError(clientErr);
      return;
    }
    setSubmitting(true);
    try {
      let saved: AdminPracticeQuestion;
      if (mode === "edit") {
        if (!questionId) throw new Error("edit mode requires questionId");
        saved = await updateAdminQuestion(questionId, draft);
        track("admin.question.updated", {
          question_id_prefix: saved.id.slice(0, 8),
          // Minimal-blast-radius changed_fields: on client we don't have
          // the backend `diffFieldNames` output, so we emit "all-form"
          // and let the server-side audit log tell the full story.
          // The structure is still type-safe (string[]).
          changed_fields: ["client_submitted"],
        });
      } else {
        saved = await createAdminQuestion(draft);
        track("admin.question.created", {
          state_code: saved.state_code as
            | "CA"
            | "TX"
            | "FL"
            | "NY"
            | "NJ"
            | "IL"
            | "MA"
            | "WA"
            | "AZ"
            | "GA",
          locale: saved.locale as
            | "en"
            | "es"
            | "hi"
            | "te"
            | "tl"
            | "ru"
            | "uk"
            | "zh-Hans",
          category: saved.category,
        });
      }
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as
          | { error?: string; field?: string; message?: string }
          | null;
        const mapped = resolveErrorField(body?.field);
        setFieldError({
          field: mapped ?? "form",
          message: body?.message ?? err.message,
        });
      } else {
        setFieldError({
          field: "form",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const errorFor = (key: string): string | null =>
    fieldError?.field === key ? fieldError.message : null;

  // Expose the draft so page-level preview can render without a prop drill.
  // Uses useEffect (not useMemo) so the notification fires after commit —
  // setState-during-render is a React violation. All the in-component
  // mutators ALSO call onDraftChange synchronously, so this effect is
  // belt-and-braces for cases where `initial` was mutated externally.
  useEffect(() => {
    onDraftChange?.(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="admin-editor-form"
      className="space-y-lg"
      noValidate
    >
      {/* State */}
      <fieldset className="rounded-md border border-brand-primary/10 bg-white p-md">
        <legend className="px-xs text-xs font-semibold uppercase tracking-wide text-neutral-600">
          {t("admin.editor.fields.stateCode")}
        </legend>
        <div className="flex flex-wrap gap-sm">
          {activeStates().map((s) => (
            <label
              key={s.code}
              className="inline-flex items-center gap-xs rounded-md border border-neutral-200 px-sm py-xs text-sm cursor-pointer has-[:checked]:border-brand-primary has-[:checked]:bg-brand-primary/5"
            >
              <input
                type="radio"
                name={`${radioGroupName}-state`}
                value={s.code}
                checked={draft.state_code === s.code}
                onChange={() => update({ state_code: s.code })}
              />
              {s.code}
            </label>
          ))}
        </div>
      </fieldset>

      {/* Locale */}
      <fieldset className="rounded-md border border-brand-primary/10 bg-white p-md">
        <legend className="px-xs text-xs font-semibold uppercase tracking-wide text-neutral-600">
          {t("admin.editor.fields.locale")}
        </legend>
        <div className="flex flex-wrap gap-sm">
          {LANGUAGES.map((l) => (
            <label
              key={l.code}
              className="inline-flex items-center gap-xs rounded-md border border-neutral-200 px-sm py-xs text-sm cursor-pointer has-[:checked]:border-brand-primary has-[:checked]:bg-brand-primary/5"
            >
              <input
                type="radio"
                name={`${radioGroupName}-locale`}
                value={l.code}
                checked={draft.locale === l.code}
                onChange={() => update({ locale: l.code })}
              />
              {l.nativeName} ({l.code})
            </label>
          ))}
        </div>
      </fieldset>

      {/* Category */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide text-neutral-600">
          {t("admin.editor.fields.category")}
        </label>
        <select
          data-testid="admin-editor-category"
          value={draft.category}
          onChange={(ev) => update({ category: ev.currentTarget.value })}
          className="mt-xs w-full rounded-md border border-neutral-300 bg-white px-sm py-xs text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {CATEGORY_ENUM.map((c) => (
            <option key={c} value={c}>
              {t(`admin.category.${c.toLowerCase()}`, {
                defaultValue: c.replaceAll("_", " "),
              })}
            </option>
          ))}
        </select>
      </div>

      {/* Difficulty slider */}
      <div>
        <label
          htmlFor={difficultyId}
          className="block text-xs font-semibold uppercase tracking-wide text-neutral-600"
        >
          {t("admin.editor.fields.difficulty")}
        </label>
        <div className="mt-xs flex items-center gap-sm">
          <input
            id={difficultyId}
            data-testid="admin-editor-difficulty"
            type="range"
            min={1}
            max={5}
            step={1}
            value={draft.difficulty}
            aria-invalid={!!errorFor("difficulty")}
            aria-describedby={errorFor("difficulty") ? difficultyErrId : undefined}
            onChange={(ev) =>
              update({ difficulty: Number(ev.currentTarget.value) })
            }
            className="flex-1"
          />
          <span
            data-testid="admin-editor-difficulty-readout"
            className="w-32 text-right text-sm text-neutral-700"
          >
            {t("admin.editor.fields.difficultyValue", { n: draft.difficulty })}
          </span>
        </div>
        {errorFor("difficulty") && (
          <p
            id={difficultyErrId}
            role="alert"
            aria-live="polite"
            className="mt-xs text-xs text-danger"
          >
            {errorFor("difficulty")}
          </p>
        )}
      </div>

      {/* Question text */}
      <div>
        <label
          htmlFor={qTextId}
          className="block text-xs font-semibold uppercase tracking-wide text-neutral-600"
        >
          {t("admin.editor.fields.questionText")}
        </label>
        <textarea
          id={qTextId}
          data-testid="admin-editor-question-text"
          value={draft.question_text}
          rows={3}
          maxLength={MAX_QUESTION_TEXT}
          aria-invalid={!!errorFor("question_text")}
          aria-describedby={errorFor("question_text") ? qTextErrId : undefined}
          onChange={(ev) => update({ question_text: ev.currentTarget.value })}
          className="mt-xs w-full rounded-md border border-neutral-300 bg-white p-sm text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        />
        <p
          data-testid="admin-editor-question-count"
          className="mt-xs text-xs text-neutral-600"
        >
          {t("admin.editor.fields.charCount", {
            current: draft.question_text.length,
            max: MAX_QUESTION_TEXT,
          })}
        </p>
        {errorFor("question_text") && (
          <p
            id={qTextErrId}
            role="alert"
            aria-live="polite"
            data-testid="admin-editor-error-question-text"
            className="mt-xs text-xs text-danger"
          >
            {errorFor("question_text")}
          </p>
        )}
      </div>

      {/* Choices */}
      <fieldset className="rounded-md border border-brand-primary/10 bg-white p-md">
        <legend className="px-xs text-xs font-semibold uppercase tracking-wide text-neutral-600">
          {t("admin.editor.fields.choices")}
        </legend>
        <p className="mb-sm text-xs text-neutral-600">
          {t("admin.editor.fields.choicesHelp")}
        </p>
        <div className="space-y-sm">
          {draft.choices.map((c, i) => (
            <ChoiceEditor
              key={`${c.id}-${i}`}
              index={i}
              choice={c}
              radioGroupName={`${radioGroupName}-correct`}
              isCorrect={draft.correct_choice_id === c.id}
              onTextChange={onChoiceText}
              onSelectCorrect={onSelectCorrect}
              onRemove={onRemoveChoice}
              canRemove={draft.choices.length > MIN_CHOICES}
              disabled={submitting}
            />
          ))}
        </div>
        <button
          type="button"
          data-testid="admin-editor-add-choice"
          disabled={submitting || draft.choices.length >= MAX_CHOICES}
          onClick={onAddChoice}
          className="mt-sm inline-flex items-center rounded-md border border-brand-primary/40 bg-white px-sm py-xs text-sm font-medium text-brand-primary transition-colors hover:bg-brand-primary/5 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("admin.editor.fields.addChoice")}
        </button>
        {(errorFor("choices") || errorFor("correct_choice_id")) && (
          <p
            id={choicesErrId}
            role="alert"
            aria-live="polite"
            data-testid="admin-editor-error-choices"
            className="mt-xs text-xs text-danger"
          >
            {errorFor("choices") ?? errorFor("correct_choice_id")}
          </p>
        )}
      </fieldset>

      {/* Explanation */}
      <div>
        <label
          htmlFor={explanationId}
          className="block text-xs font-semibold uppercase tracking-wide text-neutral-600"
        >
          {t("admin.editor.fields.explanation")}
        </label>
        <textarea
          id={explanationId}
          data-testid="admin-editor-explanation"
          value={draft.explanation}
          rows={4}
          maxLength={MAX_EXPLANATION}
          aria-invalid={!!errorFor("explanation")}
          aria-describedby={errorFor("explanation") ? explanationErrId : undefined}
          onChange={(ev) => update({ explanation: ev.currentTarget.value })}
          className="mt-xs w-full rounded-md border border-neutral-300 bg-white p-sm text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        />
        <p
          data-testid="admin-editor-explanation-count"
          className="mt-xs text-xs text-neutral-600"
        >
          {t("admin.editor.fields.charCount", {
            current: draft.explanation.length,
            max: MAX_EXPLANATION,
          })}
        </p>
        {errorFor("explanation") && (
          <p
            id={explanationErrId}
            role="alert"
            aria-live="polite"
            data-testid="admin-editor-error-explanation"
            className="mt-xs text-xs text-danger"
          >
            {errorFor("explanation")}
          </p>
        )}
      </div>

      {/* Handbook section */}
      <div>
        <label
          htmlFor={handbookId}
          className="block text-xs font-semibold uppercase tracking-wide text-neutral-600"
        >
          {t("admin.editor.fields.handbookSection")}
        </label>
        <input
          id={handbookId}
          data-testid="admin-editor-handbook"
          type="text"
          value={draft.handbook_section ?? ""}
          onChange={(ev) =>
            update({ handbook_section: ev.currentTarget.value || null })
          }
          className="mt-xs w-full rounded-md border border-neutral-300 bg-white px-sm py-xs text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        />
      </div>

      {fieldError?.field === "form" && (
        <p
          role="alert"
          aria-live="polite"
          data-testid="admin-editor-error-form"
          className="rounded-md border border-danger/40 bg-danger/5 p-sm text-sm text-danger"
        >
          {fieldError.message}
        </p>
      )}

      <div className="flex gap-sm">
        <button
          type="submit"
          data-testid="admin-editor-submit"
          disabled={submitting}
          className="inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {submitting
            ? t("admin.editor.submit.saving")
            : mode === "edit"
              ? t("admin.editor.submit.update")
              : t("admin.editor.submit.create")}
        </button>
      </div>
    </form>
  );
}

/** Helper for callers: convert a full `AdminPracticeQuestion` fetched from
 *  the server into the `AdminPracticeQuestionInput` shape the form owns. */
export function toFormInput(
  q: AdminPracticeQuestion,
): AdminPracticeQuestionInput {
  const { id: _id, created_at_ms: _c, ...rest } = q;
  void _id;
  void _c;
  return rest;
}

export { QuestionPreviewCard };
